import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { ServiceStandardsListResult } from "@/modules/service-standard/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { serviceStandardApi } from "../api/client";
import {
  serviceStandardKeys,
  useDeleteServiceStandard,
  useDuplicateServiceStandard,
  useServiceStandardsQuery,
} from "../api/queries";
import { ServiceStandardTour, useServiceStandardTour } from "../tour/ServiceStandardTour";
import { ServiceStandardFilter } from "../ui/ServiceStandardFilter";
import { ServiceStandardTable } from "../ui/ServiceStandardTable";
import { ServiceStandardToolbar } from "../ui/ServiceStandardToolbar";

export function ServiceStandardListPage() {
  const nav = useNavigate({ from: "/app/service-standard" });
  const search: OffsetPageQueryDTO = useSearch({ strict: false });
  const qc = useQueryClient();
  const tour = useServiceStandardTour();

  const list = useServiceStandardsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["service_standard:create"]);

  const activateMutation = useMutation({
    mutationFn: (id: string) =>
      serviceStandardApi.update(id, { status: "active" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceStandardKeys.all });
      toast.success("ເປີດໃຊ້ງານສໍາເລັດ");
    },
    onError: () => {
      toast.error("ເປີດໃຊ້ງານລົ້ມເຫຼວ");
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      serviceStandardApi.update(id, { status: "inactive" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceStandardKeys.all });
      toast.success("ປິດໃຊ້ງານສໍາເລັດ");
    },
    onError: () => {
      toast.error("ປິດໃຊ້ງານລົ້ມເຫຼວ");
    },
  });

  const handleActivate = (item: ServiceStandardsListResult["data"][number]) => {
    activateMutation.mutate(item.id);
  };

  const handleDeactivate = (
    item: ServiceStandardsListResult["data"][number],
  ) => {
    deactivateMutation.mutate(item.id);
  };

  const duplicateMutation = useDuplicateServiceStandard();

  const handleCopy = async (id: string) => {
    try {
      const duplicated = await duplicateMutation.mutateAsync(id);
      if (duplicated?.id) {
        nav({ to: "/app/service-standard/$id", params: { id: duplicated.id } });
      }
    } catch {
      // Error is already handled by the mutation
    }
  };

  const deleteMutation = useDeleteServiceStandard();

  const handleDelete = (item: ServiceStandardsListResult["data"][number]) => {
    deleteMutation.mutate(item.id);
  };

  return (
    <>
      <Header />

      <Main>
        <ServiceStandardTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <ServiceStandardToolbar
          canManage={!!canManage}
          onCreate={() => {
            nav({ to: "/app/service-standard/new" });
          }}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ServiceStandardFilter />

          <ServiceStandardTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) => {
              nav({
                search: {
                  ...search,
                  offset,
                  limit,
                },
              });
            }}
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) => {
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              });
            }}
            onEdit={(standard) => {
              nav({
                to: "/app/service-standard/$id/edit",
                params: { id: standard.id },
              });
            }}
            onView={(id) => {
              nav({ to: "/app/service-standard/$id", params: { id } });
            }}
            onCopy={handleCopy}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onDelete={handleDelete}
          />
        </div>
      </Main>
    </>
  );
}
